import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CruiseReservation, useAddTripItem } from "@/hooks/useTrips";
import { useAutoEnrichCruise } from "@/hooks/useCruiseEnrichment";
import { toast } from "sonner";

const cruiseSchema = z.object({
  cruise_line: z.string().min(1, "Cruise line is required"),
  ship_name: z.string().min(1, "Ship name is required"),
  departure_port: z.string().min(1, "Departure port is required"),
  departure_date: z.string().min(1, "Departure date is required"),
  arrival_port: z.string().optional(),
  arrival_date: z.string().optional(),
  cabin_number: z.string().optional(),
  cabin_type: z.string().optional(),
  deck: z.string().optional(),
  confirmation_number: z.string().optional(),
});

type CruiseFormData = z.infer<typeof cruiseSchema>;

interface AddCruiseDialogProps {
  tripId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddCruiseDialog = ({
  tripId,
  open,
  onOpenChange,
}: AddCruiseDialogProps) => {
  const addItem = useAddTripItem();
  const autoEnrich = useAutoEnrichCruise();

  const form = useForm<CruiseFormData>({
    resolver: zodResolver(cruiseSchema),
    defaultValues: {
      cruise_line: "",
      ship_name: "",
      departure_port: "",
      departure_date: "",
      arrival_port: "",
      arrival_date: "",
      cabin_number: "",
      cabin_type: "",
      deck: "",
      confirmation_number: "",
    },
  });

  const onSubmit = (data: CruiseFormData) => {
    const cruise: CruiseReservation = {
      ...data,
      departure_port: data.departure_port,
      departure_date: data.departure_date,
    };

    addItem.mutate(
      {
        tripId,
        itemType: "cruises",
        item: cruise,
      },
      {
        onSuccess: () => {
          toast.success("Cruise added");
          form.reset();
          onOpenChange(false);

          // Auto-enrich after adding - fetch trip to find the new cruise index
          autoEnrich.mutate({
            tripId,
            cruiseIndex: 0, // Will be the latest; enrichment hook re-fetches trip
            cruise,
          });
        },
        onError: (error) => {
          toast.error(`Failed to add: ${error.message}`);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Cruise</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="cruise_line"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cruise Line</FormLabel>
                    <FormControl>
                      <Input placeholder="Royal Caribbean" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="ship_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ship Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Symphony of the Seas" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="departure_port"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Departure Port</FormLabel>
                    <FormControl>
                      <Input placeholder="Miami, FL" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="arrival_port"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Arrival Port</FormLabel>
                    <FormControl>
                      <Input placeholder="Miami, FL" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="departure_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Departure Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="arrival_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Arrival Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="cabin_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cabin #</FormLabel>
                    <FormControl>
                      <Input placeholder="8210" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cabin_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cabin Type</FormLabel>
                    <FormControl>
                      <Input placeholder="Balcony" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="deck"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Deck</FormLabel>
                    <FormControl>
                      <Input placeholder="8" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="confirmation_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirmation #</FormLabel>
                  <FormControl>
                    <Input placeholder="ABC123" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addItem.isPending}>
                {addItem.isPending ? "Adding..." : "Add Cruise"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
